// src/pages/auth/ForgotPassword.jsx
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import toast from 'react-hot-toast'
import api from '@/utils/axios'
import { GraduationCap, Mail, Lock, Eye, EyeOff, Loader2, CheckCircle2, ArrowLeft } from 'lucide-react'

export default function ForgotPassword() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const emailFromUrl = searchParams.get('email')

  // If token + email in URL, show reset form; otherwise show request form
  const hasResetParams = !!token && !!emailFromUrl

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0d2757] to-[#1a3a6b] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-400 rounded-2xl mb-4 shadow-xl">
            <GraduationCap size={32} className="text-[#0d2757]" />
          </div>
          <h1 className="text-3xl font-bold text-white">
            educ<span className="text-orange-400">za</span>
          </h1>
          <p className="text-white/60 mt-2 text-sm">School Management Platform</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {hasResetParams ? (
            <ResetPasswordForm token={token} email={emailFromUrl} />
          ) : (
            <RequestResetForm />
          )}
        </div>
      </div>
    </div>
  )
}

// ── Step 1: Request Password Reset ───────────────────────────────────────────
function RequestResetForm() {
  const [sent, setSent] = useState(false)
  const [sentEmail, setSentEmail] = useState('')
  const { register, handleSubmit, formState: { errors } } = useForm()

  const mutation = useMutation(
    async (data) => {
      const { data: res } = await api.post('/auth/forgot', { email: data.email })
      return res
    },
    {
      onSuccess: (_, variables) => {
        setSentEmail(variables.email)
        setSent(true)
        toast.success('Reset link sent! Check your email.')
      },
      onError: (err) => {
        const msg = err.response?.data?.message || 'Failed to send reset link. Please try again.'
        toast.error(msg)
      },
    }
  )

  if (sent) {
    return (
      <div className="text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 size={32} className="text-green-600" />
        </div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">Check your email</h2>
        <p className="text-sm text-gray-500 mb-6">
          We sent a password reset link to <strong className="text-gray-700">{sentEmail}</strong>.
          Click the link in the email to reset your password.
        </p>
        <p className="text-xs text-gray-400 mb-6">
          Didn't receive the email? Check your spam folder or{' '}
          <button onClick={() => setSent(false)} className="text-[#0d2757] font-medium hover:underline">
            try again
          </button>
        </p>
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm text-[#0d2757] font-semibold hover:underline"
        >
          <ArrowLeft size={14} /> Back to Sign In
        </Link>
      </div>
    )
  }

  return (
    <>
      <h2 className="text-xl font-bold text-gray-800 mb-2">Forgot password?</h2>
      <p className="text-sm text-gray-500 mb-6">
        Enter your email address and we'll send you a link to reset your password.
      </p>

      <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
          <div className="relative">
            <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              {...register('email', {
                required: 'Email is required',
                pattern: { value: /\S+@\S+\.\S+/, message: 'Enter a valid email' },
              })}
              type="email"
              placeholder="your@email.com"
              className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0d2757] focus:border-transparent"
            />
          </div>
          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
        </div>

        <button
          type="submit"
          disabled={mutation.isLoading}
          className="w-full bg-[#0d2757] text-white rounded-lg py-3 text-sm font-semibold hover:bg-[#1a3a6b] transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {mutation.isLoading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <Mail size={16} />
          )}
          Send Reset Link
        </button>
      </form>

      <p className="text-center text-sm text-gray-500 mt-6">
        Remember your password?{' '}
        <Link to="/login" className="text-orange-500 font-semibold hover:underline">
          Sign in
        </Link>
      </p>
    </>
  )
}

// ── Step 2: Reset Password ───────────────────────────────────────────────────
function ResetPasswordForm({ token, email }) {
  const [done, setDone] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const { register, handleSubmit, watch, formState: { errors } } = useForm()
  const password = watch('password')

  const mutation = useMutation(
    async (data) => {
      const { data: res } = await api.post('/auth/reset', {
        email,
        token,
        password: data.password,
        password_confirmation: data.password_confirmation,
      })
      return res
    },
    {
      onSuccess: () => {
        setDone(true)
        toast.success('Password reset successfully!')
      },
      onError: (err) => {
        const msg = err.response?.data?.message || 'Failed to reset password. The link may have expired.'
        toast.error(msg)
      },
    }
  )

  if (done) {
    return (
      <div className="text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 size={32} className="text-green-600" />
        </div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">Password Reset!</h2>
        <p className="text-sm text-gray-500 mb-6">
          Your password has been successfully reset. You can now sign in with your new password.
        </p>
        <Link
          to="/login"
          className="inline-block w-full bg-[#0d2757] text-white rounded-lg py-3 text-sm font-semibold hover:bg-[#1a3a6b] transition-colors text-center"
        >
          Sign In
        </Link>
      </div>
    )
  }

  return (
    <>
      <h2 className="text-xl font-bold text-gray-800 mb-2">Reset your password</h2>
      <p className="text-sm text-gray-500 mb-6">
        Enter a new password for <strong className="text-gray-700">{email}</strong>
      </p>

      <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
          <div className="relative">
            <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'Minimum 8 characters' },
              })}
              type={showPassword ? 'text' : 'password'}
              placeholder="Minimum 8 characters"
              className="w-full border border-gray-300 rounded-lg pl-10 pr-10 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0d2757] focus:border-transparent"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
          <div className="relative">
            <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              {...register('password_confirmation', {
                required: 'Please confirm your password',
                validate: (val) => val === password || 'Passwords do not match',
              })}
              type={showConfirm ? 'text' : 'password'}
              placeholder="Re-enter your password"
              className="w-full border border-gray-300 rounded-lg pl-10 pr-10 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0d2757] focus:border-transparent"
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password_confirmation && (
            <p className="text-red-500 text-xs mt-1">{errors.password_confirmation.message}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={mutation.isLoading}
          className="w-full bg-[#0d2757] text-white rounded-lg py-3 text-sm font-semibold hover:bg-[#1a3a6b] transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {mutation.isLoading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <Lock size={16} />
          )}
          Reset Password
        </button>
      </form>

      <p className="text-center text-sm text-gray-500 mt-6">
        <Link to="/login" className="text-[#0d2757] font-semibold hover:underline flex items-center justify-center gap-1">
          <ArrowLeft size={14} /> Back to Sign In
        </Link>
      </p>
    </>
  )
}
